import { loanProducts, blogPosts, faqs, cities, cityLoanSlugs } from "../mock/data";

export const SITE = "https://loansupportindia.com";
export const OG_IMAGE = `${SITE}/logo-full.png`;
const SUFFIX = " | Loansupportindia";

// Static per-path SEO (India loan-lead optimised)
const STATIC = {
  "/": {
    title: "Loansupportindia – Personal, Business & MSME Loans in India",
    description:
      "Compare and apply for personal loans, business loans, MSME loans and loan against property from authorised lending partners across India. Quick, transparent loan assistance — approval and terms are decided by the lender.",
  },
  "/about": {
    title: "About Us – Loan Facilitation Platform in India" + SUFFIX,
    description:
      "Loansupportindia is a loan and financial-services facilitation platform connecting borrowers in India with authorised lending partners for personal, business and MSME loans.",
  },
  "/contact": {
    title: "Contact Us – Loan Enquiry & Support" + SUFFIX,
    description:
      "Get in touch with Loansupportindia for personal, business, MSME loans and loan against property enquiries across India. Submit an enquiry or reach us on WhatsApp.",
  },
  "/career": {
    title: "Careers" + SUFFIX,
    description: "Join Loansupportindia and help Indians access suitable loan and financial products responsibly.",
  },
  "/insurance": {
    title: "Insurance – Health, Life & Motor Cover in India" + SUFFIX,
    description:
      "Explore health, life and motor insurance options via authorised insurers/intermediaries under the applicable IRDAI framework. Policies subject to terms.",
  },
  "/credit-score": {
    title: "Check Your Credit Score & Report (India)" + SUFFIX,
    description:
      "Understand your credit score (300–900) and check your credit report responsibly, with consent. A good score may help your loan eligibility in India.",
  },
  "/calculators/emi": {
    title: "EMI Calculator – Personal, Home & Business Loan EMI" + SUFFIX,
    description:
      "Free online EMI calculator for personal, home, business and MSME loans in India. Estimate your monthly EMI, total interest and repayment instantly.",
  },
  "/calculators/gst": {
    title: "GST Calculator – Add or Remove GST Online" + SUFFIX,
    description: "Free GST calculator to add or remove GST (3%, 5%, 12%, 18%, 28%) for invoices and billing in India.",
  },
  "/faqs": {
    title: "Loan FAQs – Eligibility, Process & Consent" + SUFFIX,
    description:
      "Frequently asked questions about loan facilitation, eligibility, data consent, interest rates and grievance redressal for borrowers in India.",
  },
  "/blog": {
    title: "Knowledge Centre – Loans, Credit & Insurance Guides" + SUFFIX,
    description:
      "Practical guides on personal loans, home loans, MSME finance, credit score and insurance for borrowers in India.",
  },
  "/lending-partners": {
    title: "Lending Partners – Banks & NBFCs" + SUFFIX,
    description: "Our authorised lending partners (Banks/NBFCs) for personal, business, MSME loans and loan against property in India.",
  },
  "/insurance-partners": {
    title: "Insurance Partners" + SUFFIX,
    description: "Authorised insurance partners offered under the applicable IRDAI framework in India.",
  },
  "/investors": {
    title: "Investors & Stakeholders" + SUFFIX,
    description: "Investor and stakeholder information for Loansupportindia, a loan facilitation platform in India.",
  },
  "/company-information": {
    title: "Company & Regulatory Information" + SUFFIX,
    description: "Company identity and regulatory information for Loansupportindia, a loan and financial-services facilitation platform in India.",
  },
  "/grievance-redressal": {
    title: "Grievance Redressal" + SUFFIX,
    description: "Grievance redressal process and officer details for Loansupportindia. Raise and escalate complaints about our services.",
  },
  "/privacy-policy": {
    title: "Privacy Policy" + SUFFIX,
    description: "How Loansupportindia collects, uses and protects your information, with need-based data collection and explicit consent.",
  },
  "/terms": {
    title: "Terms & Conditions" + SUFFIX,
    description: "Terms and conditions governing the use of the Loansupportindia loan facilitation platform.",
  },
  "/disclaimer": {
    title: "Disclaimer" + SUFFIX,
    description: "Important disclaimer: Loansupportindia is a facilitator and not the lender. Approval and terms are decided by the lender.",
  },
  "/cookie-policy": {
    title: "Cookie Policy" + SUFFIX,
    description: "How Loansupportindia uses cookies and similar technologies, including analytics and marketing where enabled.",
  },
};

function orgNode() {
  return {
    "@type": "FinancialService",
    name: "Loansupportindia",
    url: SITE,
    areaServed: "IN",
  };
}

function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: SITE + it.path,
    })),
  };
}

export function getSeo(pathname) {
  // Dynamic: city loan landing pages (/loans/:slug/:city)
  const cityMatch = pathname.match(/^\/loans\/([^/]+)\/([^/]+)$/);
  if (cityMatch) {
    const [, slug, citySlug] = cityMatch;
    const p = loanProducts[slug];
    const city = cities.find((c) => c.slug === citySlug);
    if (p && city && cityLoanSlugs.includes(slug)) {
      const title = `${p.name} in ${city.name} – Apply Online${SUFFIX}`;
      const description = `Apply online for a ${p.name.toLowerCase()} in ${city.name} with assistance from authorised lending partners. Check eligibility, documents and EMI. Interest rate and approval are decided by the lender.`;
      return {
        title, description, path: pathname, image: p.image || OG_IMAGE,
        jsonLd: [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: p.name,
            name: `${p.name} in ${city.name} – Loansupportindia`,
            description,
            areaServed: { "@type": "City", name: city.name },
            provider: orgNode(),
          },
          breadcrumb([
            { name: "Home", path: "/" },
            { name: p.name, path: `/loans/${slug}` },
            { name: city.name, path: pathname },
          ]),
        ],
      };
    }
  }

  // Dynamic: loan product pages
  if (pathname.startsWith("/loans/")) {
    const slug = pathname.split("/")[2];
    const p = loanProducts[slug];
    if (p) {
      const title = `${p.name} in India – Apply Online${SUFFIX}`;
      const description = `Apply online for a ${p.name.toLowerCase()} in India with assistance from authorised lending partners. Check eligibility, documents and EMI. Interest rate, approval and terms are decided by the lender.`;
      return {
        title, description, path: pathname, image: p.image || OG_IMAGE,
        jsonLd: [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: p.name,
            name: `${p.name} – Loansupportindia`,
            description,
            areaServed: "IN",
            provider: orgNode(),
          },
          breadcrumb([{ name: "Home", path: "/" }, { name: "Loans", path: "/loans/personal-loan" }, { name: p.name, path: pathname }]),
        ],
      };
    }
  }

  // Dynamic: blog posts
  if (pathname.startsWith("/blog/")) {
    const slug = pathname.split("/")[2];
    const post = blogPosts.find((b) => b.slug === slug);
    if (post) {
      return {
        title: `${post.title}${SUFFIX}`,
        description: post.excerpt,
        path: pathname,
        image: post.image || OG_IMAGE,
        type: "article",
        jsonLd: [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.image,
            articleSection: post.category,
            publisher: orgNode(),
            mainEntityOfPage: SITE + pathname,
          },
        ],
      };
    }
  }

  const base = STATIC[pathname] || {
    title: "Loan & Financial Services Platform in India | Loansupportindia",
    description:
      "Compare and apply for personal, business and MSME loans from authorised lending partners across India with Loansupportindia.",
  };

  const extra = {};
  if (pathname === "/faqs") {
    extra.jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }

  return { ...base, path: pathname, image: OG_IMAGE, ...extra };
}
